/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useState, useEffect, Suspense } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';

import ApiInfoBar from '../../shared/ApiInfoBar';
import { useStyles } from './style';
import { groupObjectsByKey, isValidData } from '../../../data/utils';
import {
  ApplicationState,
  Document,
  DocumentsReducer,
  ApiReducer,
  UserReducer,
} from '../../../data/interfaces';
import { getApiDocumentContent } from '../../../data/operations';
import { setErrorNotification } from '../../../store/actions/notification';
import { TabPanel } from '../../shared/Tab/TabPanel';
import Markdown from '../../shared/Markdown/Markdown';

interface ApiDocumentationProps {
  organizationHandle: string,
  apiReducer: ApiReducer;
  userReducer: UserReducer;
  documentsReducer: DocumentsReducer;
}

function ApiDocumentation(props: ApiDocumentationProps) {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { documentsReducer, userReducer, organizationHandle } = props;
  const { apimTokenObtained } = userReducer;
  const { documents: docInfo } = documentsReducer;
  const [content, setContent] = useState("");
  const [selectedTab, setSelectedTab] = useState<number>(0);

  let documents: Document[] = [];

  if (isValidData(docInfo)) {
    documents = docInfo.list;
  }
  const { MARKDOWN: markdownDocsList }: {MARKDOWN: Document[]} =
    groupObjectsByKey(documents, "sourceType");

  useEffect(() => {
    if (markdownDocsList && markdownDocsList.length !== 0) {
      onDocSelect(0);
    }
  }, [documents]);

  const getMarkdownContent = async (id: string) => {
    try {
      const response = await getApiDocumentContent(id, false, apimTokenObtained);
      setContent(response?.data);
    } catch (error) {
      dispatch(
        setErrorNotification(
          intl.formatMessage({
            id: "api.apiDocumentation.messages.errorNoDocumentContent",
            defaultMessage: "Error while fetching the document content",
          })
        )
      );
    }
  };

  const onDocSelect = async (tabIndex: number) => {
    const selectedDoc = markdownDocsList[tabIndex];
    if (isValidData(selectedDoc)) {
      await getMarkdownContent(markdownDocsList[tabIndex].documentId);
      setSelectedTab(tabIndex);
    } else {
      dispatch(
        setErrorNotification(
          intl.formatMessage({
            id: "api.apiDocumentation.messages.errorNoDocument",
            defaultMessage: "The requested document does not exists",
          })
        )
      );
    }
  };

  return (
    <Box className={classes.content}>
      <ApiInfoBar />
      <Box px={6.5}>
        <Box className={classes.tabsBox}>
          <Tabs
            value={selectedTab}
            onChange={(_event, value) => onDocSelect(value)}
            TabIndicatorProps={{ className: classes.tabs }}
          >
            {markdownDocsList?.map((document: Document) => (
              <Tab
                key={document.documentId}
                disableRipple
                disableFocusRipple
                label={
                  <Tooltip title={document.name}>
                    <Typography className={classes.tabTextParent}>
                      {document.name}
                    </Typography>
                  </Tooltip>
                }
                classes={{
                  wrapper: classes.tabTitleText,
                  root: classes.tab,
                }}
              />
            ))}
          </Tabs>
        </Box>
        {markdownDocsList?.map((document: Document, index) => (
          <TabPanel
            selectedIndex={selectedTab}
            index={index}
            key={document.documentId}
          >
            <Box
              mt={2}
              className={classes.markdownViewWrapper}
              textAlign={"justify"}
              pl={2}
            >
              <Suspense fallback={<CircularProgress />}>
                <Markdown mdText={content} />
              </Suspense>
            </Box>
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    apiReducer: state.apiReducer,
    userReducer: state.userReducer,
    documentsReducer: state.documentsReducer,
  };
};

export default connect(mapStateToProps)(ApiDocumentation);
